import React, { useEffect, useState, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { getAuthConfig, getApiBaseUrl } from '../../services/configService';
import './ARReport.css';

const ARReport = () => {

  const [balances, setBalances] = useState([]);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [arData, setArData] = useState([]);

  const [loadingBalances, setLoadingBalances] = useState(true);
  const [loadingTable, setLoadingTable] = useState(false);

  const [search, setSearch] = useState('');
  const [tableSearch, setTableSearch] = useState('');

  const [employeeFilter, setEmployeeFilter] = useState('all');

  const [sortField, setSortField] = useState('Hospital_Shortname');
  const [sortDir, setSortDir] = useState('asc');

  const [tableSortField, setTableSortField] = useState('balance');
  const [tableSortDir, setTableSortDir] = useState('asc');
  
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  const { instance, accounts } = useMsal();
  const authConfig = getAuthConfig();
  const navigate = useNavigate();

  const getToken = async () => {
    try {
      const res = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    } catch {
      const res = await instance.acquireTokenPopup({
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    }
  };

  /* ======================
     FETCH SIDEBAR DATA
  ====================== */

  useEffect(() => {
    const fetchBalances = async () => {
      const token = await getToken();
      const res = await fetch(`${getApiBaseUrl()}/client-balances`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      setBalances(json || []);
      setLoadingBalances(false);
    };

    fetchBalances();
  }, []);

  /* ======================
     FETCH TABLE DATA
  ====================== */

  const fetchAR = async (revId) => {
    setSelectedHospital(revId);
    setLoadingTable(true);

    const token = await getToken();
    const res = await fetch(
      `${getApiBaseUrl()}/ar-report?hospitals=${revId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const json = await res.json();
    setArData(json.data || []);
    setLoadingTable(false);
  };

  /* ======================
     SIDEBAR FILTER + SORT
  ====================== */

  const filteredSidebar = useMemo(() => {
    const q = search.toLowerCase();

    return balances
      .filter(h =>
        h.Hospital_Shortname.toLowerCase().includes(q)
      )
      .sort((a, b) => {
        let val = 0;

        if (sortField === 'balance') {
          val = b.balance_totals.balance - a.balance_totals.balance;
        } else {
          val = a.Hospital_Shortname.localeCompare(b.Hospital_Shortname);
        }

        return sortDir === 'asc' ? -val : val;
      });

  }, [balances, search, sortField, sortDir]);

 
  /* ======================
     TABLE FILTERING
  ====================== */

  const filteredTable = useMemo(() => {
    const q = tableSearch.toLowerCase();

    return arData.filter(r => {

      if (employeeFilter === 'employee' && r.client_is_employee !== 1) return false;
      if (employeeFilter === 'non' && r.client_is_employee === 1) return false;

      const match = Object.values(r)
        .join(' ')
        .toLowerCase()
        .includes(q);

      return match;
    });

  }, [arData, tableSearch, employeeFilter]);

  const summary = useMemo(() => {
    const totalClients = filteredTable.length;
  
    const empCount = filteredTable.filter(r => r.client_is_employee === 1).length;
  
    const balance = filteredTable.reduce((a, r) => a + (r.balance || 0), 0);
    const curr = filteredTable.reduce((a, r) => a + (r.currentmonth || 0), 0);
    const a30 = filteredTable.reduce((a, r) => a + (r.aged30 || 0), 0);
    const a60 = filteredTable.reduce((a, r) => a + (r.aged60 || 0), 0);
    const a90 = filteredTable.reduce((a, r) => a + (r.aged90 || 0), 0);
  
    return {
      totalClients,
      empCount,
      pct: totalClients ? Math.round(empCount / totalClients * 100) : 0,
      balance, curr, a30, a60, a90
    };
  }, [filteredTable]);


  useEffect(() => {
    setPage(1);
  }, [tableSearch, employeeFilter, selectedHospital]);
  

  const sortedTable = useMemo(() => {
    return [...filteredTable].sort((a, b) => {
      const aVal = a[tableSortField];
      const bVal = b[tableSortField];
  
      if (!aVal) return 1;
      if (!bVal) return -1;
  
      if (typeof aVal === 'number') {
        return tableSortDir === 'desc'
          ? bVal - aVal
          : aVal - bVal;
      }
  
      return tableSortDir === 'desc'
        ? String(bVal).localeCompare(String(aVal))
        : String(aVal).localeCompare(String(bVal));
    });
  }, [filteredTable, tableSortField, tableSortDir]);
  

  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedTable.slice(start, start + PAGE_SIZE);
  }, [sortedTable, page]);  

  /* ======================
     CSV EXPORT
  ====================== */

  const exportCsv = () => {
    const rows = filteredTable;

    if (!rows.length) return;

    const keys = Object.keys(rows[0]);

    const csv = [
      keys.join(','),
      ...rows.map(r =>
        keys.map(k => `"${r[k] ?? ''}"`).join(',')
      )
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'ar_report.csv';
    a.click();
  };

  const selectedHospitalData = useMemo(() => {
    return balances.find(h => h.revenue_id === selectedHospital);
  }, [balances, selectedHospital]);
  
  return (
    <div className="ar-report">

      {/* SIDEBAR */}
      <div className="ar-sidebar">


      <div className="ar-sidebar-header">
        <button
          className="back-btn-auth"
          onClick={() => navigate(-1)}
          aria-label="Go back"
          title="Back"
        >
          <FaArrowLeft />
        </button>

        <input
        placeholder="Search hospitals..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        />

        <div className="ar-sort">
        <button
            className={sortField === 'Hospital_Shortname' ? 'active' : ''}
            onClick={() => setSortField('Hospital_Shortname')}
        >
            Name
        </button>

        <button
            className={sortField === 'balance' ? 'active' : ''}
            onClick={() => setSortField('balance')}
        >
            Balance
        </button>

        <button
            className="sort-dir"
            onClick={() => setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')}
        >
            {sortDir === 'asc' ? '↑' : '↓'}
        </button>
        </div>
    </div>

    <div className="ar-sidebar-list">
        {filteredSidebar.map(h => {
          const pct =
            h.client_count > 0
              ? (h.is_employee_count / h.client_count * 100).toFixed(0)
              : 0;

          return (
            <div
              key={h.revenue_id}
              className={`ar-card ${selectedHospital === h.revenue_id ? 'selected' : ''}`}
              onClick={() => fetchAR(h.revenue_id)}
            >
              <div className="ar-title">{h.Hospital_Shortname}</div>

              <div className="ar-meta">
                {h.client_count} clients · {h.is_employee_count} emp ({pct}%)
              </div>

              <div className="ar-balance">
                ${h.balance_totals.balance.toLocaleString()}
              </div>
            </div>
          );
        })}

      </div>
    </div>
      {/* RIGHT PANEL */}
      <div className="ar-main">

        {!selectedHospital && (
            <div className="ar-instructions">

                <div className="ar-intro">
                <h2>Accounts Receivable Workbench</h2>
                <p>
                    Analyze outstanding client balances by hospital. Select a hospital from the left panel to explore account-level details,
                    review aging, and identify collection opportunities.
                </p>
                </div>

                <div className="ar-instruction-grid">

                <div className="ar-instruction-card">
                    <div className="step">1</div>
                    <div>
                    <strong>Select a Hospital</strong>
                    <p>
                        Use the sidebar to browse hospitals. Click any hospital card to load its full Accounts Receivable breakdown.
                    </p>
                    </div>
                </div>

                <div className="ar-instruction-card">
                    <div className="step">2</div>
                    <div>
                    <strong>Search & Sort</strong>
                    <p>
                        Use the search box to quickly find a hospital by name.
                        Sort by <b>Name</b> or <b>Total Balance</b> and toggle direction (↑↓)
                        to surface high-priority locations.
                    </p>
                    </div>
                </div>

                <div className="ar-instruction-card">
                    <div className="step">3</div>
                    <div>
                    <strong>Review AR Details</strong>
                    <p>
                        Once selected, view client-level balances, aging buckets, and payment history.
                        The summary bar highlights totals and aging distribution for quick assessment.
                    </p>
                    </div>
                </div>

                <div className="ar-instruction-card">
                    <div className="step">4</div>
                    <div>
                    <strong>Filter, Analyze & Export</strong>
                    <p>
                        Search across all fields, filter employee accounts, sort columns, and export
                        results to CSV for deeper analysis or follow-up actions.
                    </p>
                    </div>
                </div>

                </div>

                </div>

        )}

        {selectedHospital && (
          <div className="workbench">

            <div className="workbench-header">                    
              <div className="workbench-header-title">
                AR Report for <span>{selectedHospitalData?.Hospital_Shortname}</span>
              </div>
              <div className="workbench-controls">
                <input
                  placeholder="Search table..."
                  value={tableSearch}
                  onChange={e => setTableSearch(e.target.value)}
                />

                <select
                  value={employeeFilter}
                  onChange={e => setEmployeeFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="employee">Employees</option>
                  <option value="non">Non-Employees</option>
                </select>
              </div>

              <button className="export-btn" onClick={exportCsv}>
                Export CSV
              </button>
            </div>

            <div className="ar-summary">
                <div><b>Clients:</b> {summary.totalClients}</div>
                <div><b>Employees:</b> {summary.empCount} ({summary.pct}%)</div>
                <div><b>Total:</b> ${summary.balance.toLocaleString()}</div>
                <div><b>Current:</b> ${summary.curr.toLocaleString()}</div>
                <div><b>30:</b> ${summary.a30.toLocaleString()}</div>
                <div><b>60:</b> ${summary.a60.toLocaleString()}</div>
                <div><b>90:</b> ${summary.a90.toLocaleString()}</div>
            </div>

            {loadingTable ? (
              <div>Loading...</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th onClick={() => setTableSortField('firstname')}>Name</th>
                    <th onClick={() => setTableSortField('email_address')}>Email</th>
                    <th onClick={() => setTableSortField('balance')}>Balance</th>
                    <th onClick={() => setTableSortField('currentmonth')}>Current</th>
                    <th onClick={() => setTableSortField('aged30')}>30</th>
                    <th onClick={() => setTableSortField('aged60')}>60</th>
                    <th onClick={() => setTableSortField('aged90')}>90</th>
                    <th onClick={() => setTableSortField('Last_Payment_Date')}>Last Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((r, i) => (
                    <tr key={i}>     
                      <td>{r.firstname} {r.lastname}</td>
                      <td>{r.email_address}</td>
                      <td>${r.balance}</td>
                      <td>${r.currentmonth}</td>
                      <td>${r.aged30}</td>
                      <td>${r.aged60}</td>
                      <td>${r.aged90}</td>
                      <td>{r.Last_Payment_Date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="pagination">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
            <span>Page {page}</span>
            <button
                disabled={page * PAGE_SIZE >= sortedTable.length}
                onClick={() => setPage(p => p + 1)}
            >
                Next
            </button>
            </div>

          </div>
        )}

      </div>

    </div>
  );
};

export default ARReport;
